// Curated demo videos — full lesson format (>120s), visually rich.
//
// The ten lessons the home page shows off: the POPULAR LESSONS row, and the preview chip
// that cycles through them beside the composer. They are the same ten on purpose — the row
// fetches every cover eagerly, which is what lets the chip show one as a poster for free.
//
// The ids are stable published lessons. `topic` doubles as the title the typewriter spells
// out, so it is the human phrasing rather than the stored title.
//
// THE ORDER IS THE RUNNING ORDER, and it is a choice, not an accident. The chip plays this
// list from the top and the row prints it left to right, so the first three entries are
// what a visitor sees before deciding whether any of this works — they are the ones with
// the most happening on screen at their opening frame. Derivatives and Integrals sit at the
// END: their openings are near-identical (an axis and a curve, at the same moment, in the
// same blue) so back to back they read as the page repeating itself, and they are the
// quietest of the ten.
//
// Nothing indexes into this array, so reordering it is safe — HomePage only iterates. The
// one thing it changes besides the running order is WHICH CLIP PAYS THE COLD START: entry
// zero is the only one with no lookahead behind it (see `start` below), so its opening
// frame is the only seek a visitor ever waits on. Fourier at 4.19s is 271KB to first paint,
// the dearest of the three candidates for the slot (Neural Networks 209KB, Black Holes
// 216KB) — it is also the busiest picture, which is what costs the bytes and what makes it
// worth leading with.
/* `start` IS WHERE THE CHIP OPENS THIS LESSON, in seconds, and it is per-video on purpose.
 *
 *  The chip used to open every one of these at a flat 1.5s (`DEMO_PREVIEW_START_SECONDS`,
 *  still the fallback). A lesson opens on its title card being written onto an empty frame,
 *  and measured across all ten, every one is 0.00% lit at t=0 and only 0.6–1.8% lit at 1.5s.
 *  Exactly one of them — "The Meaning of a Derivative" — has a picture by then, and it is
 *  the lesson the comment in constants.js reasons about. It was also the chip's opening clip
 *  when that number was chosen, so 1.5s was fitted to a sample of one, and the sample was
 *  whichever lesson happened to be first.
 *
 *  EVERY VALUE IS A KEYFRAME. Seeking costs a byte-range request plus a decode forward from
 *  the preceding keyframe, so landing on one means decoding a single self-contained image.
 *  That only MATTERS for the first clip — `useDemoReel` warms the next one ~4.5s ahead, so
 *  the other nine never pay their seek on screen — but it costs nothing to hold the rule.
 *
 *  DO NOT AUTOMATE THIS AS "SNAP TO THE NEAREST KEYFRAME". 80 of the 358 keyframes across
 *  these ten are blank: a scene cuts to black and the content follows a beat later, and the
 *  black one compresses to almost nothing, so the CHEAPEST keyframe is reliably the worst
 *  one to show. The rule is "the cheapest keyframe worth looking at", which needs the moov
 *  atom and so cannot be evaluated at runtime.
 *
 *  All ten are measured, so the 1.5s fallback now fires only for an id swapped in later —
 *  which is the honest behaviour for a lesson nobody has looked at. */
export const DEMO_VIDEO_IDS = [
  { id: "46746053-6f69-491c-abf7-fa951e915ccb", topic: "Visualizing the Fourier Transform", start: 4.19 },
  { id: "6a4bdaa2-8839-4496-9952-2330e65e16aa", topic: "How Neural Networks Learn",         start: 4.19 },
  { id: "fab56cef-5edd-4c87-a78f-21d1216224f9", topic: "How Black Holes Bend Light",        start: 4.19 },
  { id: "dbb90050-8c60-497e-8f65-b95caf1b40f3", topic: "What Eigenvalues Actually Mean",    start: 4.19 },
  { id: "54a53bec-1469-4320-969b-264efaa8021e", topic: "Why 0.999 Repeating Equals 1",      start: 20.29 },
  { id: "8657457a-7729-42f5-9e5b-4f9328e91078", topic: "How GPS Uses Relativity",           start: 4.19 },
  { id: "12b602ed-c2e0-422e-a327-914905d3eeb2", topic: "Photosynthesis Step by Step",       start: 4.19 },
  { id: "88c5062b-1959-4ba1-8ff6-d3808ceecf66", topic: "The Beauty of Euler's Formula",     start: 4.19 },
  { id: "3826940a-47c1-48ec-a0ae-a5bdf96896a2", topic: "The Meaning of a Derivative",       start: 4.19 },
  { id: "487dad00-e11b-4071-9685-69d90bb7d84d", topic: "How Integration Actually Works",    start: 8.36 },
];

/** Where this demo opens, or undefined if it has never been measured. Looked up by id rather
 *  than read off the record, because the record the reel holds has been through an enrichment
 *  pass and a lookup is the one spelling that cannot go stale. */
const START_BY_ID = Object.fromEntries(
  DEMO_VIDEO_IDS.filter((d) => d.start != null).map((d) => [d.id, d.start]),
);
export const demoStartSeconds = (id) => START_BY_ID[id];

/* THE SHAPE THE REST OF THE APP EXPECTS — a video record, so `extractThumbnailUrl` and the
   row's cards read these exactly as they read a video from the API. HomePage seeds its
   `sampleVideos` state with this array and later ENRICHES each entry with
   `thumbnail_storage_path` and `storage_path` from /meta; it never replaces the list, so
   index 0 is always index 0 and the chip always opens on the same lesson. */
export const DEMO_VIDEOS = DEMO_VIDEO_IDS.map((d) => ({
  job_id: d.id,
  video_type: "educational",
  topic: d.topic,
  title: d.topic,
}));
